import { useState, useMemo, useEffect } from 'react';
import { Check, ArrowLeft, Flag, Globe2, Loader2, Plus } from 'lucide-react';
import { Field } from '../components/ui/Field';
import { Button } from '../components/ui/Button';
import { FileInput } from '../components/ui/FileInput';
import { MultiFileInput } from '../components/ui/MultiFileInput';
import { PageHeader } from '../components/PageHeader';
import { IMPOSTOS_LIST, STATUS_LIST } from '../lib/constants';
import {
  formatBRLInput, parseBRLInput, formatPtaxInput, parsePtaxInput,
  todayISO, validateEmailList,
  formatCNPJ, validateCNPJ,
} from '../lib/formatters';
import { fetchPtaxCompra } from '../lib/bcb';
import { buildExteriorDescricao } from '../lib/exterior-descricao';

// Avanca uma data ISO ('YYYY-MM-DD') em N meses preservando o dia.
// Quando o dia nao existe no mes destino (ex: 31/jan + 1 mes), usa o
// ultimo dia do mes destino (28/29/fev ou 30 conforme o caso).
// Usado pra calcular vencimentos e competencias das parcelas/recorrencias.
// Exportado pra App.jsx tambem usar no momento de inserir as linhas.
export function addMonthsISO(iso, months) {
  if (!iso || typeof iso !== 'string') return '';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const totalIdx = (m - 1) + months;          // mes 0-based + offset
  const targetY  = y + Math.floor(totalIdx / 12);
  const targetM  = ((totalIdx % 12) + 12) % 12;  // 0-based
  // Ultimo dia do mes destino
  const lastDay  = new Date(targetY, targetM + 1, 0).getDate();
  const day      = Math.min(d, lastDay);
  return `${targetY}-${String(targetM + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

const blank = (empresas, servicos, forceExterior = false) => ({
  pi: '',
  // Default em branco quando ha mais de uma empresa pra escolher (modo
  // principal) — forca o usuario a selecionar e evita pedir nota pra
  // empresa errada por descuido. Em modo replica, a lista chega com
  // uma empresa so, entao pre-seleciona pra nao obrigar clique extra.
  empresa_id: empresas.length === 1 ? empresas[0].id : '',
  tomador_id: '',
  servico_id: servicos[0]?.id || '',
  unidade_negocio_id: null,
  data_solicitacao: todayISO(),
  // Em forceExterior (LLCs) a data de competencia comeca VAZIA — assim
  // o auto-fetch da PTAX so dispara quando o usuario realmente seleciona
  // a data, em vez de buscar logo no mount com a data de hoje.
  data_prestacao_servico: forceExterior ? '' : todayISO(),
  previsao_recebimento: '',
  valor: 0,
  valor_str: '',
  // Exterior: cotacao PTAX + valor em USD; quando is_exterior=true, o
  // campo `valor` e calculado por (ptax * valor_dolar) e fica read-only.
  // Em replicas LLCs (forceExterior=true), ja inicia em exterior.
  is_exterior: !!forceExterior,
  ptax: 0,
  ptax_str: '',
  valor_dolar: 0,
  valor_dolar_str: '',
  descricao: '',
  solicitante: '',
  numero_nota: '',
  impostos_retidos: ['Sem retenção'],
  informacoes_complementares: '',
  anexo_nota_url: '',
  emails_envio: '',
  possui_intermediario: false,
  intermediario_cnpj: '',
  intermediario_razao_social: '',
  anexo_intermediario_url: '',
  anexo_complementar_1_url: '',
  anexo_complementar_2_url: '',
  // Array de { url, filename } — usado quando useDocsAuxiliaresMulti.
  documentos_auxiliares: [],
  // Serie (recorrencia ou parcelamento) — controles do form. Esses
  // campos NAO sao salvos diretamente nas linhas geradas; o App.jsx
  // usa pra decidir se cria 1 ou N linhas no submit.
  serie_tipo: 'unico',     // 'unico' | 'recorrencia' | 'parcelamento'
  serie_quantidade: 1,     // 2-12 quando nao for unico
  status: 'Solicitada',
});

export function SolicitacaoForm({
  initial,
  empresas, tomadores, servicos, unidades,
  forceExterior = false,
  onSubmit, onCancel, onError,
  // Callbacks pra abrir o modal de cadastro de tomador dentro do form
  // e receber de volta o id quando o novo tomador for criado.
  onCreateTomador,
  newlyCreatedTomadorId,
  onConsumeNewTomadorId,
  // Em replicas que definem empresa+unidade fixas (ex: Supersonic), trava
  // os dois campos: preenche automaticamente e renderiza como readonly
  // (cinza, sem dropdown). Evita o usuario selecionar uma unidade errada
  // da mesma empresa (ETUS tem outras unidades alem da Supersonic).
  lockedEmpresaId = null,
  lockedUnidadeId = null,
  // Em modo Exterior, libera o usuario a digitar o Valor (R$) diretamente
  // sem precisar PTAX + USD. Se preencher PTAX + USD, auto-calculo segue
  // sobrescrevendo o R$ (comportamento original). Hoje so BHAZ usa.
  freeExteriorValue = false,
  // Esconde o picker de Recorrencia/Parcelamento — o form sempre cria
  // 1 lancamento. A logica de serie no servidor segue intacta, eh so a
  // UI que some. Hoje so BHAZ esconde.
  hideSerieControls = false,
  // Troca 'Documento auxiliar' por 2 slots complementares (comp1 e comp2).
  // Hoje so BHAZ ativa.
  useAnexosComplementares = false,
  // Ativa compressao de PDF no browser antes de subir pro storage.
  // Hoje so BHAZ ativa.
  compressPdfUploads = false,
  // Layout novo: 1 campo pra Nota + 1 campo multi pra documentos auxiliares
  // (array jsonb documentos_auxiliares). Substitui Doc aux + Relatorios +
  // Cartas quando ligado. Prioriza sobre useAnexosComplementares.
  useDocsAuxiliaresMulti = false,
  // BHAZ-only: layout laranja pra Nota fiscal + campo PI (uso interno,
  // reusa anexo_intermediario_url) lado a lado. Se falso, renderiza
  // Nota fiscal simples sem destaque. Independente do multi acima.
  useNotaPILayout = false,
}) {
  const [form, setForm] = useState(() => {
    if (initial) {
      return {
        ...initial,
        valor_str: '',
        ptax_str: '',
        valor_dolar_str: '',
        // Em replica forceExterior, qualquer solicitacao editada tambem
        // entra em modo exterior — pra nao quebrar o fluxo da LLCs.
        is_exterior: forceExterior ? true : !!initial.is_exterior,
        ptax: Number(initial.ptax) || 0,
        valor_dolar: Number(initial.valor_dolar) || 0,
        // Em replicas que travam empresa+unidade (Supersonic), forca os
        // valores fixos mesmo em edicao — garante que ninguem migrou pra
        // outra unidade da mesma empresa por engano.
        ...(lockedEmpresaId ? { empresa_id: lockedEmpresaId } : {}),
        ...(lockedUnidadeId ? { unidade_negocio_id: lockedUnidadeId } : {}),
      };
    }
    const base = blank(empresas, servicos, forceExterior);
    if (lockedEmpresaId) base.empresa_id = lockedEmpresaId;
    if (lockedUnidadeId) base.unidade_negocio_id = lockedUnidadeId;
    return base;
  });
  const [errors, setErrors] = useState({});
  const [ptaxLoading, setPtaxLoading] = useState(false);
  // Flag que liga assim que o usuario edita manualmente a descricao —
  // dai a gente para de auto-sobrescrever com o template. Em modo edit
  // (initial != null) ja comeca true, pra nao zoar o texto existente.
  const [userEditedDesc, setUserEditedDesc] = useState(!!initial?.descricao);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Auto-seleciona o tomador recem-cadastrado a partir do botao '+ Novo
  // tomador'. App.jsx empurra o id via prop quando o modal de cadastro
  // salva; aqui captura e marca como consumido pra nao re-disparar.
  useEffect(() => {
    if (!newlyCreatedTomadorId) return;
    setForm(f => ({ ...f, tomador_id: newlyCreatedTomadorId }));
    onConsumeNewTomadorId?.();
  }, [newlyCreatedTomadorId, onConsumeNewTomadorId]);

  // Em modo exterior, recalcula o valor (R$) automatico a cada mudanca
  // de PTAX ou USD. Sem cleanup; nao depende de `valor`, entao nao loop.
  // Em modo freeExteriorValue (BHAZ): so auto-calcula quando AMBOS PTAX e
  // USD foram preenchidos. Caso algum esteja zero, NAO sobrescreve o R$
  // (deixa o usuario digitar direto se quiser).
  useEffect(() => {
    if (!form.is_exterior) return;
    const ptax  = Number(form.ptax) || 0;
    const dolar = Number(form.valor_dolar) || 0;
    if (freeExteriorValue && (ptax === 0 || dolar === 0)) return;
    const v = ptax * dolar;
    // Arredonda pra 2 casas pra evitar lixo de ponto flutuante.
    const rounded = Math.round(v * 100) / 100;
    setForm(f => (f.valor === rounded ? f : { ...f, valor: rounded, valor_str: '' }));
  }, [form.is_exterior, form.ptax, form.valor_dolar, freeExteriorValue]);

  // Auto-fetch da PTAX (compra) do BCB sempre que a data de competencia
  // mudar em modo exterior. So roda em forceExterior pra nao surpreender
  // usuario do Brasil que poderia tambem ter is_exterior=true por outro
  // motivo. O usuario pode override manualmente depois.
  useEffect(() => {
    if (!forceExterior) return;
    if (!form.is_exterior) return;
    if (!form.data_prestacao_servico) return;
    let cancelled = false;
    setPtaxLoading(true);
    fetchPtaxCompra(form.data_prestacao_servico).then(result => {
      if (cancelled) return;
      setPtaxLoading(false);
      if (result && Number.isFinite(result.rate) && result.rate > 0) {
        // Arredonda pra 4 casas (BCB ja devolve assim, defensivo)
        const rounded = Math.round(result.rate * 10000) / 10000;
        setForm(f => ({ ...f, ptax: rounded, ptax_str: '' }));
      }
    }).catch(() => { if (!cancelled) setPtaxLoading(false); });
    return () => { cancelled = true; };
  }, [forceExterior, form.is_exterior, form.data_prestacao_servico]);

  // Auto-preenchimento da descricao com o template Exterior. Roda a cada
  // mudanca dos campos dependentes — exceto quando o usuario ja editou
  // manualmente (flag userEditedDesc).
  useEffect(() => {
    if (!forceExterior) return;
    if (!form.is_exterior) return;
    if (userEditedDesc) return;
    const next = buildExteriorDescricao({
      dataPrestacao: form.data_prestacao_servico,
      valorDolar: form.valor_dolar,
      ptax: form.ptax,
      previsaoRec: form.previsao_recebimento,
    });
    setForm(f => (f.descricao === next ? f : { ...f, descricao: next }));
  }, [
    forceExterior,
    form.is_exterior,
    userEditedDesc,
    form.data_prestacao_servico,
    form.valor_dolar,
    form.ptax,
    form.previsao_recebimento,
  ]);

  const tomadoresFiltrados = useMemo(() => {
    // "Vinculados" inclui:
    //   - tomadores da empresa selecionada (vinculo direto)
    //   - tomadores SEM empresa (empresa_id null) — sao "clientes em todas
    //     as empresas" e devem aparecer no topo de qualquer picker
    const isVinculado = (t) => !t.empresa_id || t.empresa_id === form.empresa_id;
    const vinculados = tomadores.filter(isVinculado);
    const outros = tomadores.filter(t => !isVinculado(t));
    return [...vinculados, ...outros];
  }, [tomadores, form.empresa_id]);

  // Picker so mostra servicos ativos. Se a solicitacao em edicao usa um
  // servico que foi desativado depois, mantem ele na lista pra nao perder
  // a referencia (mas na pratica o nome do servico continua aparecendo).
  const servicosVisiveis = useMemo(() => {
    const ativos = servicos.filter(s => s.ativo !== false);
    if (form.servico_id && !ativos.some(s => s.id === form.servico_id)) {
      const orig = servicos.find(s => s.id === form.servico_id);
      if (orig) return [...ativos, orig];
    }
    return ativos;
  }, [servicos, form.servico_id]);

  const toggleImposto = (imp) => {
    setForm(f => {
      let next;
      if (imp === 'Sem retenção') {
        next = ['Sem retenção'];
      } else {
        const has = f.impostos_retidos.includes(imp);
        const filtered = f.impostos_retidos.filter(i => i !== 'Sem retenção');
        next = has ? filtered.filter(i => i !== imp) : [...filtered, imp];
        if (next.length === 0) next = ['Sem retenção'];
      }
      return { ...f, impostos_retidos: next };
    });
  };

  const handleSubmit = () => {
    const e = {};
    if (!form.empresa_id) e.empresa_id = 'Selecione';
    if (!form.tomador_id) e.tomador_id = 'Selecione';
    if (!form.servico_id) e.servico_id = 'Selecione';
    if (form.is_exterior) {
      if (freeExteriorValue) {
        // Modo livre (BHAZ): so exige o valor R$ final (digitado ou calculado).
        // PTAX e USD ficam opcionais.
        if (!(Number(form.valor) > 0)) e.valor = 'Informe o valor';
      } else {
        if (!(Number(form.ptax) > 0)) e.ptax = 'Informe a PTAX';
        if (!(Number(form.valor_dolar) > 0)) e.valor_dolar = 'Informe o valor em US$';
      }
    } else {
      if (!(Number(form.valor) > 0)) e.valor = 'Informe o valor';
    }
    if (!form.descricao.trim()) e.descricao = 'Obrigatório';
    if (form.descricao.length > 2000) e.descricao = 'Máx. 2000 caracteres';
    if (!form.solicitante.trim()) e.solicitante = 'Obrigatório';
    if (form.emails_envio && !validateEmailList(form.emails_envio)) e.emails_envio = 'Algum e-mail está inválido';
    if (form.possui_intermediario) {
      if (!form.intermediario_razao_social?.trim()) e.intermediario_razao_social = 'Obrigatório';
      if (!validateCNPJ(form.intermediario_cnpj)) e.intermediario_cnpj = 'CNPJ inválido';
    }
    setErrors(e);
    if (Object.keys(e).length === 0) {
      const { valor_str: _v, ptax_str: _p, valor_dolar_str: _d, ...payload } = form;
      onSubmit({
        ...payload,
        valor: Number(form.valor),
        // Em modo Brasil zera os campos de exterior pra nao manter lixo
        // se o usuario tinha alternado e voltou.
        ptax: form.is_exterior ? Number(form.ptax) : null,
        valor_dolar: form.is_exterior ? Number(form.valor_dolar) : null,
      });
    }
  };

  const SectionHeader = ({ children }) => (
    <div style={{
      gridColumn: '1 / -1', fontSize: 11.5, fontWeight: 700, letterSpacing: '.08em',
      textTransform: 'uppercase', color: 'var(--muted)', marginTop: 8, marginBottom: -4,
      display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <span>{children}</span>
      <span style={{ height: 1, background: 'var(--border)', flex: 1 }} />
    </div>
  );

  const valorDisplay = form.valor_str !== ''
    ? form.valor_str
    : (form.valor ? formatBRLInput(String(Math.round(Number(form.valor) * 100))) : '');

  const ptaxDisplay = form.ptax_str !== ''
    ? form.ptax_str
    : (form.ptax ? formatPtaxInput(String(Math.round(Number(form.ptax) * 10000))) : '');

  const dolarDisplay = form.valor_dolar_str !== ''
    ? form.valor_dolar_str
    : (form.valor_dolar ? formatBRLInput(String(Math.round(Number(form.valor_dolar) * 100))) : '');

  return (
    <div className="fade-in" style={{ maxWidth: 920, margin: '0 auto' }}>
      <PageHeader
        title={
          initial?.__isCopy
            ? 'Duplicar solicitação'
            : initial
              ? 'Editar solicitação'
              : 'Nova solicitação'
        }
        subtitle={
          initial?.__isCopy
            ? 'Dados pré-preenchidos a partir do lançamento original — altere o que precisar e salve'
            : initial?.codigo_unico
              ? `ID ${initial.codigo_unico} · Atualize os dados conforme necessário`
              : 'Preencha os dados — o ID será gerado automaticamente ao salvar'
        }
        actions={
          <>
            <Button variant="secondary" onClick={onCancel} icon={ArrowLeft}>Voltar</Button>
            <Button onClick={handleSubmit} icon={Check}>Salvar e gerar PDF</Button>
          </>
        }
      />

      {/* Toggle Brasil / Exterior — escondido em replicas forceExterior
          (ex: LLCs). Nesse caso o form ja inicia em exterior e nao da
          pra trocar. */}
      {!forceExterior && (
        <div style={{ marginBottom: 14 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center',
            background: 'var(--surface-2)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius)', padding: 4,
          }}>
            {[
              { key: false, label: 'Brasil',   icon: Flag },
              { key: true,  label: 'Exterior', icon: Globe2 },
            ].map(({ key, label, icon: Icon }) => {
              const active = !!form.is_exterior === !!key;
              return (
                <button
                  key={String(key)}
                  type="button"
                  onClick={() => update('is_exterior', key)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '6px 14px', fontSize: 12.5, fontWeight: 600,
                    border: 'none', borderRadius: 'calc(var(--radius) - 2px)',
                    cursor: 'pointer', fontFamily: 'inherit',
                    background: active ? 'var(--surface)' : 'transparent',
                    color: active ? 'var(--primary)' : 'var(--muted)',
                    boxShadow: active ? 'var(--shadow-sm)' : 'none',
                    transition: 'all .15s ease',
                  }}
                >
                  <Icon size={13} strokeWidth={2.2} />
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 28 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
          <SectionHeader>Identificação</SectionHeader>

          <Field label="ID da solicitação">
            <input
              className="input font-mono"
              value={initial?.codigo_unico || '— gerado ao salvar —'}
              disabled
              style={{ background: 'var(--surface-2)', color: 'var(--muted)' }}
            />
          </Field>
          <Field label="PI" helper="Opcional · identificador interno da campanha/contrato">
            <input className="input"
              value={form.pi || ''} onChange={e => update('pi', e.target.value)} placeholder="PI-2026-0000" />
          </Field>

          <Field label="Empresa emissora" required error={errors.empresa_id}
            helper={lockedEmpresaId ? 'Definido pela réplica' : undefined}>
            {lockedEmpresaId ? (
              <input
                className="input"
                value={(() => {
                  const e = empresas.find(x => x.id === lockedEmpresaId);
                  return e ? (e.nome_fantasia || e.razao_social) : '—';
                })()}
                disabled
                readOnly
              />
            ) : (
              <select className={`select ${errors.empresa_id ? 'error' : ''}`}
                value={form.empresa_id} onChange={e => update('empresa_id', e.target.value)}>
                <option value="">Selecione…</option>
                {empresas.map(e => <option key={e.id} value={e.id}>{e.nome_fantasia || e.razao_social}</option>)}
              </select>
            )}
          </Field>

          {(() => {
            const unidadesEmpresa = (unidades || []).filter(u => u.empresa_id === form.empresa_id);
            // Em replicas com unidade travada, mostra o campo readonly mesmo
            // que `unidades` venha vazio (a unidade vem direto da config da
            // replica e e fonte da verdade).
            if (lockedUnidadeId) {
              const u = (unidades || []).find(x => x.id === lockedUnidadeId);
              return (
                <Field label="Unidade de negócio" helper="Definido pela réplica">
                  <input
                    className="input"
                    value={u?.nome || '—'}
                    disabled
                    readOnly
                  />
                </Field>
              );
            }
            if (unidadesEmpresa.length === 0) return null;
            return (
              <Field label="Unidade de negócio" helper="Opcional · sub-empresa dentro da emissora">
                <select className="select"
                  value={form.unidade_negocio_id || ''}
                  onChange={e => update('unidade_negocio_id', e.target.value || null)}>
                  <option value="">— sem unidade —</option>
                  {unidadesEmpresa.map(u => (
                    <option key={u.id} value={u.id}>{u.nome}</option>
                  ))}
                </select>
              </Field>
            );
          })()}

          <Field
            label="Tomador do serviço"
            required
            error={errors.tomador_id}
            helper={onCreateTomador ? 'Não está na lista? Cadastre um novo abaixo.' : undefined}
          >
            <div style={{ display: 'flex', gap: 8 }}>
              <select
                className={`select ${errors.tomador_id ? 'error' : ''}`}
                value={form.tomador_id}
                onChange={e => update('tomador_id', e.target.value)}
                style={{ flex: 1, minWidth: 0 }}
              >
                <option value="">Selecione…</option>
                {tomadoresFiltrados.map(t => {
                  const empresa = empresas.find(e => e.id === t.empresa_id);
                  const semVinculo = !t.empresa_id;
                  const vinculadoEmpresaAtual = t.empresa_id === form.empresa_id;
                  // Sufixo so aparece quando carrega INFO util:
                  // - sem vinculo: marca que o tomador serve qualquer emissora
                  // - vinculo em OUTRA empresa: alerta que nao eh da emissora atual
                  // No caso default (vinculado a emissora atual) nao mostra nada —
                  // o "· vinculado" antes confundia ("vinculado a quem?").
                  let suffix = '';
                  if (semVinculo)                 suffix = '· cliente em todas';
                  else if (vinculadoEmpresaAtual) suffix = '';
                  else if (empresa)               suffix = `· ${empresa.nome_fantasia || empresa.razao_social}`;
                  return (
                    <option key={t.id} value={t.id}>
                      {t.nome_fantasia || t.razao_social} {suffix}
                    </option>
                  );
                })}
              </select>
              {onCreateTomador && (
                <Button
                  variant="secondary"
                  icon={Plus}
                  onClick={onCreateTomador}
                  title="Cadastrar novo tomador"
                  style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                  Novo
                </Button>
              )}
            </div>
          </Field>

          <SectionHeader>Datas e valor</SectionHeader>

          <div style={{
            gridColumn: '1 / -1', display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)', gap: 14,
          }}>
            <Field label="Data da solicitação" required>
              <input type="date" className="input" value={form.data_solicitacao}
                onChange={e => update('data_solicitacao', e.target.value)} />
            </Field>
            <Field label="Data de prestação / Competência" required>
              <input type="date" className="input" value={form.data_prestacao_servico}
                onChange={e => update('data_prestacao_servico', e.target.value)} />
            </Field>
            <Field label="Previsão de recebimento">
              <input type="date" className="input" value={form.previsao_recebimento || ''}
                onChange={e => update('previsao_recebimento', e.target.value)} />
            </Field>
          </div>

          {/* Data do recebimento — editavel manualmente. Preenchida
              automaticamente quando o cliente confirma o recebimento da
              NF (dispara 'Aguardando pagamento'), OU quando a equipe muda
              status pra 'Recebido' (App.jsx). Sempre editavel — a baixa
              real pode ter sido em outra data. */}
          {(initial?.id || form.status === 'Recebido' || form.status === 'Aguardando pagamento' || form.recebido_em) && (
            <Field label="Data do recebimento"
              helper="Preenchida automaticamente quando cliente confirma a NF ou equipe marca 'Recebido' · edite se a baixa foi em outra data">
              <input
                type="date"
                className="input"
                value={form.recebido_em ? String(form.recebido_em).slice(0, 10) : ''}
                onChange={e => {
                  const dt = e.target.value;
                  if (!dt) {
                    update('recebido_em', null);
                  } else {
                    // Preserva hora original se estava setado; senao vira meio-dia
                    // (evita quebrar em fusos horarios — meia-noite pode virar dia anterior).
                    const hora = form.recebido_em && form.recebido_em.length > 10
                      ? form.recebido_em.slice(10)
                      : 'T12:00:00.000Z';
                    update('recebido_em', dt + hora);
                  }
                }}
              />
            </Field>
          )}

          {!form.is_exterior && (
            <Field label="Valor" required error={errors.valor}>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                  color: 'var(--muted)', fontSize: 13.5, fontWeight: 600,
                }}>R$</span>
                <input
                  className={`input ${errors.valor ? 'error' : ''}`}
                  style={{ paddingLeft: 36, fontVariantNumeric: 'tabular-nums' }}
                  value={valorDisplay}
                  onChange={e => {
                    const str = formatBRLInput(e.target.value);
                    setForm(f => ({ ...f, valor_str: str, valor: parseBRLInput(e.target.value) }));
                  }}
                  placeholder="0,00"
                />
              </div>
            </Field>
          )}

          {form.is_exterior && (
            <div style={{
              gridColumn: '1 / -1', display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)', gap: 14,
            }}>
              <Field label="Cotação PTAX" required={!freeExteriorValue} error={errors.ptax}
                helper={ptaxLoading
                  ? 'Buscando cotação no BCB…'
                  : (freeExteriorValue
                      ? 'Opcional · informe pra calcular R$ automaticamente'
                      : (forceExterior ? 'Buscada do BCB pela data de competência · 4 casas' : 'R$ por US$ 1,00 · 4 casas'))
                }>
                <div style={{ position: 'relative' }}>
                  <input
                    className={`input ${errors.ptax ? 'error' : ''}`}
                    style={{
                      fontVariantNumeric: 'tabular-nums',
                      paddingRight: ptaxLoading ? 36 : undefined,
                    }}
                    value={ptaxDisplay}
                    onChange={e => {
                      const str = formatPtaxInput(e.target.value);
                      setForm(f => ({ ...f, ptax_str: str, ptax: parsePtaxInput(e.target.value) }));
                    }}
                    placeholder="0,0000"
                    disabled={ptaxLoading}
                  />
                  {ptaxLoading && (
                    <Loader2
                      size={14}
                      style={{
                        position: 'absolute', right: 12, top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'var(--muted)', animation: 'fileinput-spin 1s linear infinite',
                      }}
                    />
                  )}
                </div>
              </Field>
              <Field label="Valor em US$" required={!freeExteriorValue} error={errors.valor_dolar}
                helper={freeExteriorValue ? 'Opcional · informe pra calcular R$ automaticamente' : undefined}>
                <div style={{ position: 'relative' }}>
                  <span style={{
                    position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                    color: 'var(--muted)', fontSize: 13.5, fontWeight: 600,
                  }}>US$</span>
                  <input
                    className={`input ${errors.valor_dolar ? 'error' : ''}`}
                    style={{ paddingLeft: 42, fontVariantNumeric: 'tabular-nums' }}
                    value={dolarDisplay}
                    onChange={e => {
                      const str = formatBRLInput(e.target.value);
                      setForm(f => ({ ...f, valor_dolar_str: str, valor_dolar: parseBRLInput(e.target.value) }));
                    }}
                    placeholder="0,00"
                  />
                </div>
              </Field>
              <Field
                label="Valor (R$)"
                required={freeExteriorValue}
                error={freeExteriorValue ? errors.valor : undefined}
                helper={freeExteriorValue
                  ? 'Pode digitar direto OU preencher PTAX + US$ pra calcular'
                  : 'Calculado: PTAX × US$'}>
                <div style={{ position: 'relative' }}>
                  <span style={{
                    position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                    color: 'var(--muted)', fontSize: 13.5, fontWeight: 600,
                  }}>R$</span>
                  <input
                    className={`input ${freeExteriorValue && errors.valor ? 'error' : ''}`}
                    disabled={!freeExteriorValue}
                    style={{
                      paddingLeft: 36,
                      fontVariantNumeric: 'tabular-nums',
                      ...(freeExteriorValue ? {} : {
                        background: 'var(--surface-2)', color: 'var(--ink-2)',
                      }),
                    }}
                    value={valorDisplay}
                    onChange={freeExteriorValue ? (e) => {
                      const str = formatBRLInput(e.target.value);
                      setForm(f => ({ ...f, valor_str: str, valor: parseBRLInput(e.target.value) }));
                    } : undefined}
                    placeholder="0,00"
                    readOnly={!freeExteriorValue}
                  />
                </div>
              </Field>
            </div>
          )}

          <Field label="Serviço" required error={errors.servico_id}
            className={form.is_exterior ? 'col-span-2' : undefined}>
            <select className={`select ${errors.servico_id ? 'error' : ''}`}
              value={form.servico_id} onChange={e => update('servico_id', e.target.value)}>
              <option value="">Selecione…</option>
              {servicosVisiveis.map(s => (
                <option key={s.id} value={s.id}>
                  {s.codigo} — {s.descricao}{s.ativo === false ? ' (inativo)' : ''}
                </option>
              ))}
            </select>
          </Field>

          <SectionHeader>Detalhamento</SectionHeader>

          <Field label="Descrição" required error={errors.descricao}
            helper={`${form.descricao.length}/2000 caracteres`}
            className="col-span-2"
          >
            <textarea
              className={`textarea ${errors.descricao ? 'error' : ''}`}
              maxLength={2000}
              rows={5}
              value={form.descricao}
              onChange={e => {
                // Assim que o usuario digita, desliga o auto-fill do template
                // em forceExterior — o texto vira responsabilidade dele.
                if (forceExterior && !userEditedDesc) setUserEditedDesc(true);
                update('descricao', e.target.value);
              }}
              placeholder="Descreva resumidamente o serviço prestado…"
            />
          </Field>

          <Field label="Solicitante" required error={errors.solicitante}>
            <input className={`input ${errors.solicitante ? 'error' : ''}`}
              value={form.solicitante} onChange={e => update('solicitante', e.target.value)}
              placeholder="Nome completo" />
          </Field>
          <Field label="Status" helper="Atualize conforme a NF avança no fluxo">
            <select className="select"
              value={form.status || 'Solicitada'}
              onChange={e => update('status', e.target.value)}>
              {STATUS_LIST.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Número da nota" helper="Preenchido pela Cobrança após emissão"
            className="col-span-2">
            <input className="input font-mono" value={form.numero_nota || ''}
              onChange={e => update('numero_nota', e.target.value)} placeholder="—" />
          </Field>

          <SectionHeader>Impostos retidos</SectionHeader>

          <div style={{ gridColumn: '1 / -1', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {IMPOSTOS_LIST.map(imp => {
              const active = form.impostos_retidos.includes(imp);
              const isNone = imp === 'Sem retenção';
              return (
                <button
                  key={imp}
                  type="button"
                  onClick={() => toggleImposto(imp)}
                  style={{
                    padding: '8px 14px', borderRadius: 999, fontSize: 13, fontWeight: 600,
                    border: `1px solid ${active ? (isNone ? 'var(--border-2)' : 'var(--primary)') : 'var(--border)'}`,
                    background: active ? (isNone ? 'var(--ink)' : 'var(--primary)') : 'var(--surface)',
                    color: active
                      ? (isNone ? 'var(--ink-inverse)' : '#0A0A0A')
                      : 'var(--ink-2)',
                    cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s ease',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}
                >
                  {active && <Check size={13} strokeWidth={3} />}
                  {imp}
                </button>
              );
            })}
          </div>

          <SectionHeader>Comunicação e anexos</SectionHeader>

          <Field label="Informações complementares" className="col-span-2">
            <textarea className="textarea" rows={2} value={form.informacoes_complementares || ''}
              onChange={e => update('informacoes_complementares', e.target.value)}
              placeholder="Cláusulas, observações, dados bancários adicionais…" />
          </Field>

          <Field label="E-mail(s) para envio da nota" error={errors.emails_envio}
            helper="Separe múltiplos e-mails por vírgula" className="col-span-2">
            <input className={`input ${errors.emails_envio ? 'error' : ''}`} value={form.emails_envio || ''}
              onChange={e => update('emails_envio', e.target.value)}
              placeholder="financeiro@empresa.com, fiscal@empresa.com" />
          </Field>

          <Field label="Existe intermediário?" className="col-span-2">
            <div style={{ display: 'flex', gap: 8, maxWidth: 280 }}>
              {[
                { v: false, label: 'Não' },
                { v: true,  label: 'Sim' },
              ].map(o => {
                const active = form.possui_intermediario === o.v;
                return (
                  <button key={String(o.v)} type="button"
                    onClick={() => update('possui_intermediario', o.v)}
                    style={{
                      flex: 1, height: 40, borderRadius: 'var(--radius)',
                      border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                      background: active ? 'var(--primary-soft)' : 'var(--surface)',
                      color: active ? 'var(--primary)' : 'var(--ink-2)',
                      fontWeight: 600, fontSize: 13.5, cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}>
                    {o.label}
                  </button>
                );
              })}
            </div>
          </Field>

          {form.possui_intermediario && (
            <>
              <Field label="CNPJ do intermediário" required error={errors.intermediario_cnpj}>
                <input
                  className={`input ${errors.intermediario_cnpj ? 'error' : ''}`}
                  placeholder="00.000.000/0000-00"
                  value={form.intermediario_cnpj || ''}
                  onChange={e => update('intermediario_cnpj', formatCNPJ(e.target.value))}
                />
              </Field>
              <Field label="Razão social do intermediário" required error={errors.intermediario_razao_social}>
                <input
                  className={`input ${errors.intermediario_razao_social ? 'error' : ''}`}
                  value={form.intermediario_razao_social || ''}
                  onChange={e => update('intermediario_razao_social', e.target.value)}
                />
              </Field>
            </>
          )}

          {/* ─────────────────────────────────────────────────────────
              Recorrência ou parcelamento — só pra novos lançamentos
              (em edicao some, pra nao ofuscar que mudancas afetam so
              o item atual). Cria N lancamentos de uma vez ao salvar.
              hideSerieControls (per-replica, hoje BHAZ): esconde o bloco
              inteiro — sempre cria lancamento unico. Logica do save
              continua valida; eh so a UI que some.
              ───────────────────────────────────────────────────────── */}
          {!initial?.id && !hideSerieControls && (() => {
            const tipo = form.serie_tipo || 'unico';
            const qtd  = Math.max(1, Math.min(12, Number(form.serie_quantidade) || 1));
            const totalParcelas = tipo === 'parcelamento' ? qtd : 1;
            const valorBase = Number(form.valor) || 0;
            const valorPorItem = tipo === 'parcelamento' && qtd > 0
              ? Math.round((valorBase / qtd) * 100) / 100
              : valorBase;
            const baseVenc = form.previsao_recebimento;
            const baseComp = form.data_prestacao_servico;
            const primVenc = baseVenc || '';
            const ultVenc  = baseVenc ? addMonthsISO(baseVenc, qtd - 1) : '';
            const fmtBRL   = (n) => Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            const fmtDate  = (iso) => {
              if (!iso) return '—';
              const [yy, mm, dd] = iso.split('-');
              return `${dd}/${mm}/${yy}`;
            };

            return (
              <Field label="Recorrência ou parcelamento" className="col-span-2"
                helper="Opcional · cria múltiplos lançamentos automaticamente ao salvar">
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 8, marginBottom: 12,
                }}>
                  {[
                    { v: 'unico',         label: 'Lançamento único',     sub: 'Padrão' },
                    { v: 'recorrencia',   label: 'Recorrência mensal',   sub: 'Mesmo valor, repete N vezes' },
                    { v: 'parcelamento',  label: 'Parcelamento',         sub: 'Valor dividido em N parcelas' },
                  ].map(opt => {
                    const active = tipo === opt.v;
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => update('serie_tipo', opt.v)}
                        style={{
                          padding: '12px 14px', textAlign: 'left',
                          borderRadius: 'var(--radius)',
                          border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                          background: active ? 'var(--primary-soft)' : 'var(--surface)',
                          color: active ? 'var(--primary)' : 'var(--ink-2)',
                          cursor: 'pointer', fontFamily: 'inherit',
                          display: 'flex', flexDirection: 'column', gap: 2,
                        }}
                      >
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{opt.label}</span>
                        <span style={{ fontSize: 11, color: active ? 'var(--primary)' : 'var(--muted)' }}>{opt.sub}</span>
                      </button>
                    );
                  })}
                </div>

                {tipo !== 'unico' && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 14, alignItems: 'center' }}>
                      <input
                        type="number"
                        className="input"
                        min={2}
                        max={12}
                        value={form.serie_quantidade}
                        onChange={e => {
                          const v = Math.max(1, Math.min(12, parseInt(e.target.value, 10) || 1));
                          update('serie_quantidade', v);
                        }}
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                      />
                      <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                        {tipo === 'recorrencia' ? 'repetições' : 'parcelas'} (máx. 12) · intervalo de 1 mês
                      </span>
                    </div>

                    <div style={{
                      marginTop: 12, padding: '12px 14px',
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius)',
                      fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.55,
                    }}>
                      {tipo === 'recorrencia' && (
                        <>
                          Serão criados <strong>{qtd} lançamentos</strong> idênticos de
                          {' '}<strong>R$ {fmtBRL(valorBase)}</strong> cada
                          {baseVenc && <>, com vencimentos a cada 1 mês.<br />
                            Primeiro: <strong>{fmtDate(primVenc)}</strong> · Último: <strong>{fmtDate(ultVenc)}</strong>
                          </>}
                          {!baseVenc && <>. <em>Preencha a previsão de recebimento pra ver as datas.</em></>}
                        </>
                      )}
                      {tipo === 'parcelamento' && (
                        <>
                          Serão criadas <strong>{qtd} parcelas</strong> de
                          {' '}<strong>R$ {fmtBRL(valorPorItem)}</strong> cada
                          {' '}(total <strong>R$ {fmtBRL(valorBase)}</strong>)
                          {baseVenc && <>, com vencimentos a cada 1 mês.<br />
                            Primeira: <strong>{fmtDate(primVenc)}</strong> · Última: <strong>{fmtDate(ultVenc)}</strong>
                          </>}
                          {!baseVenc && <>. <em>Preencha a previsão de recebimento pra ver as datas.</em></>}
                          {valorBase > 0 && qtd > 1 && (Math.round(valorPorItem * qtd * 100) / 100 !== valorBase) && (
                            <div style={{ marginTop: 6, fontSize: 11.5, color: 'var(--muted)' }}>
                              ⓘ A última parcela ajusta os centavos do arredondamento pra fechar o total.
                            </div>
                          )}
                        </>
                      )}
                      {baseComp && (
                        <div style={{ marginTop: 6, fontSize: 11.5, color: 'var(--muted)' }}>
                          Competência também avança 1 mês por lançamento.
                        </div>
                      )}
                    </div>
                  </>
                )}
              </Field>
            );
          })()}

          {/* ── Bloco NOTA FISCAL (single) ─────────────────────────────
              BHAZ (useNotaPILayout): renderiza dentro de card laranja
                com PI ao lado (uso interno).
              Outras: campo simples sem destaque. */}
          {useNotaPILayout ? (
            <>
              <div style={{
                padding: 10, borderRadius: 'var(--radius)',
                background: 'rgba(245, 158, 11, 0.13)',
                border: '1px solid rgba(245, 158, 11, 0.45)',
              }}>
                <Field label="Anexo da nota fiscal emitida"
                  helper={compressPdfUploads ? 'Opcional · somente PDF · compressão automática' : 'Opcional · somente PDF'}>
                  <FileInput
                    value={form.anexo_nota_url}
                    onChange={(url) => update('anexo_nota_url', url)}
                    onError={onError}
                    onToast={onError}
                    compress={compressPdfUploads}
                  />
                </Field>
              </div>
              <div style={{
                padding: 10, borderRadius: 'var(--radius)',
                background: 'rgba(245, 158, 11, 0.05)',
                border: '1px solid rgba(245, 158, 11, 0.20)',
              }}>
                <Field label="PI"
                  helper="Opcional · somente PDF · uso interno (não vai anexado ao email)">
                  <FileInput
                    value={form.anexo_intermediario_url}
                    onChange={(url) => update('anexo_intermediario_url', url)}
                    onError={onError}
                    onToast={onError}
                    compress={compressPdfUploads}
                  />
                </Field>
              </div>
            </>
          ) : (
            <Field label="Anexo da nota fiscal emitida"
              helper={compressPdfUploads ? 'Opcional · somente PDF · compressão automática' : 'Opcional · somente PDF'}>
              <FileInput
                value={form.anexo_nota_url}
                onChange={(url) => update('anexo_nota_url', url)}
                onError={onError}
                onToast={onError}
                compress={compressPdfUploads}
              />
            </Field>
          )}

          {/* ── Bloco AUXILIARES ────────────────────────────────────────
              useDocsAuxiliaresMulti: campo multi (varios PDFs num slot)
              useAnexosComplementares: 3 slots fixos (aux + Rel + Cartas)
              default: single 'Documento auxiliar' (a menos que
                useNotaPILayout ja tenha renderizado como PI). */}
          {useDocsAuxiliaresMulti ? (
            <Field
              label="Documentos auxiliares"
              className="col-span-2"
              helper={compressPdfUploads
                ? 'Opcional · vários PDFs (relatórios, cartas, comissões…) · compressão automática · enviados junto com a nota'
                : 'Opcional · vários PDFs · enviados junto com a nota'}>
              <MultiFileInput
                value={form.documentos_auxiliares || []}
                onChange={(arr) => update('documentos_auxiliares', arr)}
                onError={onError}
                onToast={onError}
                compress={compressPdfUploads}
              />
            </Field>
          ) : (
            <>
              {/* Doc auxiliar single so quando NAO tem PI (senao seria
                  duplicado — PI ja usa anexo_intermediario_url). */}
              {!useNotaPILayout && (
                <Field label="Documento auxiliar" helper="Opcional · somente PDF · uso interno (não vai anexado ao email)">
                  <FileInput
                    value={form.anexo_intermediario_url}
                    onChange={(url) => update('anexo_intermediario_url', url)}
                    onError={onError}
                    onToast={onError}
                    compress={compressPdfUploads}
                  />
                </Field>
              )}

              {useAnexosComplementares && (
                <>
                  <Field label="Relatórios"
                    helper={compressPdfUploads
                      ? 'Opcional · somente PDF · compressão automática · enviado junto com a nota'
                      : 'Opcional · somente PDF · enviado junto com a nota'}>
                    <FileInput
                      value={form.anexo_complementar_1_url}
                      onChange={(url) => update('anexo_complementar_1_url', url)}
                      onError={onError}
                      onToast={onError}
                      compress={compressPdfUploads}
                    />
                  </Field>
                  <Field label="Cartas"
                    helper={compressPdfUploads
                      ? 'Opcional · somente PDF · compressão automática · enviado junto com a nota'
                      : 'Opcional · somente PDF · enviado junto com a nota'}>
                    <FileInput
                      value={form.anexo_complementar_2_url}
                      onChange={(url) => update('anexo_complementar_2_url', url)}
                      onError={onError}
                      onToast={onError}
                      compress={compressPdfUploads}
                    />
                  </Field>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
        <Button variant="secondary" onClick={onCancel}>Cancelar</Button>
        <Button onClick={handleSubmit} icon={Check}>Salvar e gerar PDF</Button>
      </div>

      <style>{`
        @media (max-width: 720px) {
          .card > div { grid-template-columns: 1fr !important; }
          .card > div > div[style*="grid-template-columns"] { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
